// The Meta creative editor of the creative builder: one slot's copy fields, its
// three placement formats and what those formats will run on. Split out of
// creative.tsx, which still exports it.

import { Fragment, useId, useRef, useState } from 'react';
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react';
import { SearchableDropdown } from '@/components/dropdowns';
import { isSupportedCreativeMime } from '@/api/creative-upload';
import { holdRelease } from '@/lib/release-hold';
import { RequiredField } from '@/components/required-field';
import { REQUIRED_FIELD_KEYS } from './creative-field-row';
import type { MetaMediaValidationReport } from '@/lib/media-validation/meta-validator';
import metaCreativeSpecs from '@shared/specs/meta-creative-v25.json';
import {
  disabledPlacements,
  enabledPlacements,
  placementsServedBy,
  profileIdForSlot,
  TOTAL_PLACEMENTS,
} from '@/lib/meta-placements';
import {
  metaAssetFieldKey,
  metaCopyFieldKey,
  metaDestinationFieldKey,
  metaDisplayUrlFieldKey,
  type CreativeBuilderProduction,
  type CreativeSlotAsset,
  type MetaSlotAssets,
} from '@/lib/creative-required-issues';
import {
  CHANNEL_FORMATS,
  selectOptionsFor,
  type ChannelConfig,
  type CreativeChannel,
  type CreativeField,
  type CreativeFieldValues,
  type CreativeFormat,
} from './creative-config';
import { formatDecimalSize } from '@/lib/media-validation/sizes';

/** What a builder passes down of `useRequiredFeedback()`: the messages and the attempt count. */
export interface CreativeRequiredFeedback {
  messages: Record<string, string>;
  attempt: number;
}

/**
 * What the uploads so far will actually run on, and what they will not.
 *
 * A creative may now ship one or two aspect ratios instead of all three, so the
 * consequence of a missing size has to be visible at the moment it is missing —
 * otherwise "we only had a 4:5" quietly becomes a campaign that skips Facebook
 * Feed and nobody finds out until the delivery report.
 */
export interface MetaCoveragePanelProps {
  assets: MetaSlotAssets | undefined;
  formats: readonly CreativeFormat[];
}

export function MetaCoveragePanel({ assets, formats }: MetaCoveragePanelProps) {
  const present = formats
    .map((format) => format.key || format.name)
    .filter((key) => !!assets?.[key]?.url)
    .map(profileIdForSlot);

  if (present.length === 0) {
    return (
      <div className="facet" style={{marginBottom:10}}>
        <div className="facet-hd"><h5>Placement coverage</h5></div>
        <div className="muted" style={{fontSize:10.5,lineHeight:1.5}}>
          Upload at least two formats — Meta needs more than one to customise placements.
          Each one you add turns on the placements it fits natively, and nothing is ever
          cropped or letterboxed to fill a size you did not supply.
        </div>
      </div>
    );
  }

  const enabled = enabledPlacements(present);
  const disabled = disabledPlacements(present);
  const complete = disabled.length === 0;

  return (
    <div className="facet" style={{marginBottom:10}}>
      <div className="facet-hd">
        <h5>Placement coverage</h5>
        <span className="muted" style={{fontSize:10}}>
          {enabled.length} of {TOTAL_PLACEMENTS} placements
        </span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {formats.map((format) => {
          const key = format.key || format.name;
          if (!assets?.[key]?.url) return null;
          const serves = placementsServedBy(profileIdForSlot(key), present);
          if (serves.length === 0) return null;
          return (
            <div key={key} style={{fontSize:10.5,lineHeight:1.45}}>
              <b style={{color:'var(--ink)'}}>{format.name}</b>
              <span className="muted"> → {serves.map((placement) => placement.label).join(', ')}</span>
            </div>
          );
        })}
        {complete ? (
          <div className="muted" style={{fontSize:10.5,lineHeight:1.45}}>
            Every enabled placement is covered.
          </div>
        ) : (
          <div style={{fontSize:10.5,lineHeight:1.45,color:'var(--warn)'}}>
            Not running: {disabled.map((placement) => placement.label).join(', ')}. Add the
            matching format to turn these on.
          </div>
        )}
      </div>
    </div>
  );
}

// Default Ad Name shown when a creative slot has no explicit adName set.
// Library picks override this with the library creative's own name.
// Built only from what the operator already chose. The Meta branch used to add
// " · Opening Push", matching the phrasing `metaDefaults` shipped; it is a
// campaign phase nobody declared and it is usually untrue.
export function defaultAdName(
  production: Pick<CreativeBuilderProduction, 'title'> | null | undefined,
  slotLabel: string | undefined,
  channel: CreativeChannel,
): string {
  const cfgName = (CHANNEL_FORMATS?.[channel]?.name) || (channel === 'meta' ? 'Meta' : channel === 'ctv' ? 'CTV' : 'Display');
  if (channel === 'meta') {
    return production
      ? `${production.title}${slotLabel && slotLabel !== 'Creative 1' ? ' · ' + slotLabel : ''} · ${cfgName}`
      : `Campaign${slotLabel ? ' · ' + slotLabel : ''} · ${cfgName}`;
  }
  return production
    ? `${production.title} · ${slotLabel || 'Creative'} · ${cfgName}`
    : (slotLabel || 'Creative');
}

export interface MetaCreativeEditorProps {
  channel: CreativeChannel;
  production: CreativeBuilderProduction | null | undefined;
  cfg: ChannelConfig;
  fields: readonly CreativeField[];
  /** The seeded values (`metaDefaults`); its `ad_name` is ignored, `adName` owns it. */
  initialVals?: CreativeFieldValues | null;
  /** The slot's own values. Without them the editor keeps its own. */
  formValues?: CreativeFieldValues;
  setFormValues?: (values: CreativeFieldValues) => void;
  /** The slot's media. Without them the editor keeps its own. */
  assets?: MetaSlotAssets;
  setAssets?: Dispatch<SetStateAction<MetaSlotAssets>>;
  adName?: string;
  setAdName?: (value: string) => void;
  adNameHint?: string | null;
  /** Rendered under the Ad Name field (the pinned library version). */
  libraryRef?: ReactNode;
  creativeId: string;
  requiredFeedback?: CreativeRequiredFeedback;
}

/** One uploaded file's probe: its pixel size, its length in seconds, and its object URL. */
interface MediaProbe {
  w: number;
  h: number;
  duration?: number;
  url: string;
}

export function MetaCreativeEditor({ channel, production, cfg, fields, initialVals, formValues: formValuesProp, setFormValues: setFormValuesProp, assets: assetsProp, setAssets: setAssetsProp, adName, setAdName, adNameHint, libraryRef = null, creativeId, requiredFeedback }: MetaCreativeEditorProps) {
  // Every editable Meta value is lifted into its creative slot. The local
  // fallback keeps this exported editor usable in isolation.
  const initial: CreativeFieldValues = initialVals || {};
  const { ad_name: _stripAdName, ...initialValsWithoutAdName } = initial;
  const [formValuesInner, setFormValuesInner] = useState<CreativeFieldValues>({});
  const formValues = formValuesProp ?? formValuesInner;
  const setFormValues = setFormValuesProp ?? setFormValuesInner;
  const vals: CreativeFieldValues = { ...initialValsWithoutAdName, ...formValues };
  const setVal = (k: string, v: string) => {
    if (k === 'ad_name') { setAdName?.(v); return; }
    setFormValues({ ...formValues, [k]: v });
  };
  const valFor = (k: string) => k === 'ad_name' ? (adName ?? '') : (vals[k] ?? '');
  // Each field's visible label names its control (axe `label`; the Ad Name input had none).
  const fieldIdBase = useId();

  const [assetsInner, setAssetsInner] = useState<MetaSlotAssets>(() => Object.fromEntries(cfg.formats.map(f => [f.key || f.name, null])));
  const assets = assetsProp || assetsInner;
  const setAssets = setAssetsProp || setAssetsInner;
  const filled = Object.values(assets).filter((asset): asset is CreativeSlotAsset => Boolean(asset));
  const lockedType = filled[0]?.type ?? null;
  const uploadAsset = (
    fkey: string,
    type: 'image' | 'video',
    file: File,
    probe: MediaProbe,
    validation: MetaMediaValidationReport,
  ) => {
    const ext = type === 'image' ? 'jpg' : 'mp4';
    setAssets(a => {
      const prev = a[fkey];
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return {
        ...a,
        [fkey]: {
          file,
          type,
          name: file.name || `${(production?.id || 'production')}_${fkey}.${ext}`,
          url: probe.url,
          width: probe.w,
          height: probe.h,
          durationMs: probe.duration == null ? undefined : Math.round(probe.duration * 1000),
          validation,
        },
      };
    });
  };
  const removeAsset = (fkey: string) => setAssets(a => {
    const prev = a[fkey];
    if (prev?.url) URL.revokeObjectURL(prev.url);
    return { ...a, [fkey]: null };
  });
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const openPicker = (fkey: string) => { setErrors(es => ({ ...es, [fkey]: null })); fileInputs.current[fkey]?.click(); };
  const [errors, setErrors] = useState<Record<string, { msg: string } | null>>({});
  const [validating, setValidating] = useState<Record<string, boolean>>({});
  const [validationStage, setValidationStage] = useState<Record<string, string | null>>({});

  const probeImage = (file: File) => new Promise<MediaProbe>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image. File may be corrupt.')); };
    img.src = url;
  });
  const probeVideo = (file: File) => new Promise<MediaProbe>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => resolve({ w: v.videoWidth, h: v.videoHeight, duration: v.duration, url });
    v.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read video metadata. File may be corrupt.')); };
    v.src = url;
  });

  const handleFile = async (fkey: string, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!isSupportedCreativeMime(file.type)) {
      setErrors(es => ({ ...es, [fkey]: { msg: `Unsupported file type · ${file.type || 'unknown'}. Use JPG, PNG, GIF, WebP, MP4, or MOV.` } }));
      return;
    }
    const type = file.type.startsWith('video') ? 'video' : file.type.startsWith('image') ? 'image' : null;
    if (!type) {
      setErrors(es => ({ ...es, [fkey]: { msg: `Unsupported file type · ${file.type || 'unknown'}. Use JPG, PNG, or MP4.` } }));
      return;
    }
    if (lockedType && lockedType !== type) {
      setErrors(es => ({ ...es, [fkey]: { msg: `This ad set is locked to ${lockedType.toUpperCase()}. Remove every asset to switch types.` } }));
      return;
    }
    // Taken before the validator's import, synchronously with the accept: until the file is in
    // state, a page load would drop it with nothing on screen to say so (Astra r1, 2026-10-21).
    const releaseHold = holdRelease('creative_file_accept');
    setValidating(s => ({ ...s, [fkey]: true }));
    try {
      const { validateMetaCreativeFile } = await import('@/lib/media-validation/meta-validator');
      const validation = await validateMetaCreativeFile(file, profileIdForSlot(fkey), (progress) => {
        setValidationStage(current => ({ ...current, [fkey]: progress.stage }));
      });
      if (!validation.valid) {
        const failed = validation.checks.filter(item => item.status === 'fail');
        setErrors(es => ({
          ...es,
          [fkey]: { msg: failed.map(item => `${item.label}: ${item.actual} (expected ${item.expected})`).join(' · ') },
        }));
        return;
      }
      const url = URL.createObjectURL(file);
      const probe: MediaProbe = {
        w: validation.width,
        h: validation.height,
        duration: validation.durationMs == null ? undefined : validation.durationMs / 1000,
        url,
      };
      setErrors(es => ({ ...es, [fkey]: null }));
      uploadAsset(fkey, type, file, probe, validation);
    } catch (err) {
      setErrors(es => ({ ...es, [fkey]: { msg: err instanceof Error ? err.message : String(err) } }));
    } finally {
      releaseHold();
      setValidating(s => ({ ...s, [fkey]: false }));
      setValidationStage(current => ({ ...current, [fkey]: null }));
    }
  };

  return (
    <div className="creative-stack">
      <div>
        {fields.map(f => {
          const v = valFor(f.key);
          const field = (
          <div className="field">
            <div className="flex between items-baseline" style={{marginBottom:4}}>
              <label htmlFor={`${fieldIdBase}-${f.key}`} className="field-lbl" style={{margin:0}}>{f.label}{REQUIRED_FIELD_KEYS.has(f.key) && <span style={{color:'var(--live)'}}> *</span>}{f.help && <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:8,fontSize:10.5}}>{f.help}</span>}{f.key === 'ad_name' && adNameHint && <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:8,fontSize:10.5,color:'var(--accent)'}}>· {adNameHint}</span>}</label>
              {f.max && <span className="muted mono" style={{fontSize:10, color: (v||'').length > f.max ? 'var(--warn)' : undefined}}>{(v||'').length}/{f.max}</span>}
            </div>
            {f.type === 'textarea' && <textarea id={`${fieldIdBase}-${f.key}`} className="input" rows={f.rows||2} maxLength={f.hardMax} value={v} onChange={e=>setVal(f.key, e.target.value)}/>}
            {f.type === 'input' && <input id={`${fieldIdBase}-${f.key}`} data-validation-focus={(f.key === 'dest_url' || f.key === 'display_url') ? true : undefined} type={f.key === 'dest_url' ? 'url' : 'text'} className={`input ${f.mono?'mono':''}`} style={f.mono?{fontSize:11.5}:{}} maxLength={f.hardMax} value={v} onChange={e=>setVal(f.key, e.target.value)}/>}
            {f.type === 'select' && (
              <SearchableDropdown
                ariaLabel={f.label}
                value={v}
                options={selectOptionsFor(f, v).map((option) => ({ id: option, label: option }))}
                onChange={(value) => setVal(f.key, value)}
                searchPlaceholder={`Search ${f.label.toLowerCase()}…`}
              />
            )}
            {f.key === 'ad_name' && libraryRef}
          </div>
          );
          const REQUIRED_COPY_KEYS = ['post_text', 'headline', 'cta'];
          if (
            f.key !== 'dest_url'
            && f.key !== 'display_url'
            && !REQUIRED_COPY_KEYS.includes(f.key)
          ) {
            return <Fragment key={f.key}>{field}</Fragment>;
          }
          const destinationKey = f.key === 'dest_url'
            ? metaDestinationFieldKey(creativeId)
            : f.key === 'display_url'
              ? metaDisplayUrlFieldKey(creativeId)
              : metaCopyFieldKey(creativeId, f.key);
          return (
            <RequiredField
              key={f.key}
              fieldKey={destinationKey}
              message={requiredFeedback?.messages?.[destinationKey]}
              attempt={requiredFeedback?.attempt ?? 0}
            >
              {field}
            </RequiredField>
          );
        })}
      </div>

      <div>
        <div className="field-lbl mb-2">Formats · upload at least 2 of {cfg.formats.length}</div>
        {channel === 'meta' && <MetaCoveragePanel assets={assets} formats={cfg.formats}/>}
        <div className="media-lock">
          <div>
            <div className="media-lock-lbl">Media type</div>
            <div className="media-lock-val">
              {lockedType === 'image' && <><span className="dot" style={{background:'var(--good)'}}/>LOCKED — IMAGES ONLY</>}
              {lockedType === 'video' && <><span className="dot" style={{background:'var(--good)'}}/>LOCKED — VIDEO ONLY</>}
              {!lockedType && <><span className="dot" style={{background:'var(--ink-3)'}}/>UNLOCKED — first upload sets the type</>}
            </div>
          </div>
          <div className="muted" style={{fontSize:11,textAlign:'right',maxWidth:280}}>
            {lockedType
              ? <>Every format you upload must be the same media type. Remove every asset to switch.</>
              : <>Meta ad sets cannot mix images and videos. Upload your first asset to set the type.</>}
          </div>
        </div>
        <div className="facet" style={{marginBottom:10}}>
          <div className="facet-hd"><h5>Official Meta media requirements</h5></div>
          <div className="muted" style={{fontSize:10.5,lineHeight:1.5}}>
            <b style={{color:'var(--ink)'}}>Images:</b> {metaCreativeSpecs.global.image.mime_types.join(' or ')} · max {formatDecimalSize(metaCreativeSpecs.global.image.effective_max_bytes)}.{' '}
            <b style={{color:'var(--ink)'}}>Video:</b> {metaCreativeSpecs.global.video.containers.join(' or ').toUpperCase()} · H.264 video · AAC audio when present ·
            progressive, constant frame rate, square pixels · {metaCreativeSpecs.global.video.min_duration_seconds}–{metaCreativeSpecs.global.video.max_duration_seconds}s · max {formatDecimalSize(metaCreativeSpecs.global.video.effective_max_bytes)}.
          </div>
        </div>
        <div className="grid-3" style={{gridTemplateColumns:'repeat(3,1fr)', gap:12}}>
          {cfg.formats.map(f => {
            const fkey = f.key || f.name;
            const a = assets[fkey];
            const officialSpec = metaCreativeSpecs.asset_profiles.find(
              profile => profile.id === `meta_${fkey}`,
            );
            const officialSource = metaCreativeSpecs.sources.find(
              source => officialSpec?.recommended.source_ids.includes(source.id),
            );
            const requiredKey = metaAssetFieldKey(creativeId, fkey);
            return (
              <RequiredField
                key={fkey}
                fieldKey={requiredKey}
                message={requiredFeedback?.messages?.[requiredKey]}
                attempt={requiredFeedback?.attempt ?? 0}
                surface
              >
                <div className="creative-asset" style={{aspectRatio: `${f.w}/${f.h}`, paddingTop: 18, position:'relative', overflow:'hidden'}}>
                  <div className="stripes"/>
                  <div style={{position:'absolute',top:8,left:10,fontFamily:'var(--font-display)',fontWeight:800,fontSize:11,letterSpacing:'.04em',textTransform:'uppercase',color:'var(--ink-1)'}}>{f.name}</div>
                  <div className="lbl">{f.spec}</div>
                  {a && (
                    <div style={{position:'absolute',inset:0}}>
                      {a.type === 'image'
                        ? <img src={a.url} alt={a.name} style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                        : <video src={a.url} muted loop autoPlay playsInline style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>}
                      <div style={{position:'absolute',top:0,left:0,right:0,height:60,background:'linear-gradient(to bottom, rgba(0,0,0,.55), transparent)',pointerEvents:'none'}}/>
                      <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'18px 10px 8px',background:'linear-gradient(to top, rgba(0,0,0,.7), transparent)',pointerEvents:'none'}}>
                        <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:12,color:'#fff',lineHeight:1.1}}>{f.name}</div>
                        <div style={{fontFamily:'var(--font-mono)',fontSize:9.5,color:'rgba(255,255,255,.85)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.name}</div>
                      </div>
                      <div style={{position:'absolute',top:6,left:8,fontFamily:'var(--font-mono)',fontSize:9.5,letterSpacing:'.1em',color:'#fff',textTransform:'uppercase',textShadow:'0 1px 2px rgba(0,0,0,.5)'}}>{a.type === 'image' ? '■ IMAGE' : '▶ VIDEO'}</div>
                      <button className="asset-x" onClick={()=>removeAsset(fkey)} title="Remove asset">×</button>
                    </div>
                  )}
                </div>
                <input
                  ref={el => (fileInputs.current[fkey] = el)}
                  type="file"
                  accept={lockedType === 'image' ? 'image/*' : lockedType === 'video' ? 'video/*' : 'image/*,video/*'}
                  style={{display:'none'}}
                  onChange={e=>handleFile(fkey, e)}
                />
                <div className="flex gap-1 mt-1">
                  {!a ? (
                    <button data-validation-focus className="btn btn-sm" style={{flex:1,fontSize:10}} onClick={()=>openPicker(fkey)} disabled={!!validating[fkey]}>
                      {validating[fkey] ? (validationStage[fkey] || 'Validating…') : <>↑ Upload {lockedType ? lockedType : 'image or video'}</>}
                    </button>
                  ) : (
                    <>
                      <button className="btn btn-sm" style={{flex:1,fontSize:10}} onClick={()=>openPicker(fkey)}>Replace</button>
                      <button className="btn btn-sm btn-ghost" style={{fontSize:10}} onClick={()=>removeAsset(fkey)}>Remove</button>
                    </>
                  )}
                </div>
                {officialSpec && (
                  <div className="muted" style={{fontSize:9.5,lineHeight:1.45,marginTop:5}}>
                    <b style={{color:'var(--ink-2)'}}>Required:</b>{' '}
                    {officialSpec.required.width}×{officialSpec.required.height} · ratio {officialSpec.required.aspect_ratio}
                    <br/>
                    <b style={{color:'var(--ink-2)'}}>Meta recommended:</b>{' '}
                    {officialSpec.recommended.width}×{officialSpec.recommended.height} · {officialSpec.recommended.ratio_label}
                    {officialSource && <><br/><a href={officialSource.url} target="_blank" rel="noreferrer">Official Meta specification ↗</a></>}
                  </div>
                )}
                {errors[fkey] && (
                  <div className="asset-err">
                    <span style={{flexShrink:0}}>⚠</span>
                    <span style={{flex:1}}>{errors[fkey].msg}</span>
                    <button className="asset-err-x" onClick={()=>setErrors(es=>({...es,[fkey]:null}))} title="Dismiss">×</button>
                  </div>
                )}
                {a?.validation && (
                  <div className="facet" style={{marginTop:6,padding:'7px 8px'}}>
                    <div className="facet-hd" style={{marginBottom:4}}>
                      <h5>Meta media validation</h5>
                      <span style={{color:'var(--good)',fontSize:10}}>PASS</span>
                    </div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                      {a.validation.checks.map(result => (
                        <span
                          key={result.id}
                          className={`tag ${result.status === 'warn' ? 'warn' : ''}`}
                          title={`${result.label}: ${result.actual} · expected ${result.expected}`}
                        >
                          {result.status === 'warn' ? '!' : '✓'} {result.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </RequiredField>
            );
          })}
        </div>
      </div>
    </div>
  );
}
